"use client";

import React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Menu, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import AddressAndLanguage from "../../../(root)/home/components/AddressSelect";
import { Link, useLocation, useNavigate } from "react-router";
import { CATEGORIES } from "@/lib/Constants";
import { CategoryDropdown } from "./Category-dropdown";
import { useDebouncedSearchAll } from "@/apis/Products/Queries";
import ScrollToSection from "../ScrolltoSection";
import { useGetMostWantedPreview } from "@/apis/Overall/Queries";
import { cn } from "@/lib/utils";
import CategoriesDropdown from "./categories/Categoriesdropdown";

import TopBanner from "./header/TopBanner";
import WishlistPopover from "./header/WishlistPopover";
import CartPopover from "./header/CartPopover";
import UserMenu from "./header/UserMenu";
import MobileMenu from "./header/MobileMenu";
import SearchDropdownContent from "./header/SearchDropdownContent";
import type { SearchResults } from "./header/HeaderTypes";
import { useAuthStore } from "@/store/AuthStore";

// Dummy components for demonstration purposes in a Vite environment
const MaxWidthWrapper = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("max-w-7xl mx-auto px-4 sm:px-6 lg:px-8", className)}>
    {children}
  </div>
);

const Header = () => {
  const { t } = useTranslation();
  const { data: mostWanted = [] } = useGetMostWantedPreview(8);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleLogoClick = (e: React.MouseEvent) => {
    if (pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo(0, 0);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  const desktopSearchRef = useRef<HTMLDivElement | null>(null);
  const mobileSearchRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: searchResults, isFetching } = useDebouncedSearchAll(
    debouncedSearchTerm,
    300,
    "dropdown"
  ) as {
    data: SearchResults | undefined;
    isFetching: boolean;
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      const isOutsideDesktop = desktopSearchRef.current
        ? !desktopSearchRef.current.contains(target)
        : true;
      const isOutsideMobile = mobileSearchRef.current
        ? !mobileSearchRef.current.contains(target)
        : true;

      if (isOutsideDesktop && isOutsideMobile) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, {
      passive: true,
    });
    document.addEventListener("touchstart", handleClickOutside, {
      passive: true,
    });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Suggestions (popular + recent)
  const RECENT_SEARCHES_KEY = "cm_recent_searches";
  const getRecentSearches = useCallback((): string[] => {
    try {
      if (typeof window === "undefined") return [];
      const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }, []);

  const setRecentSearches = useCallback((items: string[]) => {
    try {
      localStorage.setItem(
        RECENT_SEARCHES_KEY,
        JSON.stringify(items.slice(0, 8))
      );
    } catch { }
  }, []);

  const addRecentSearch = useCallback(
    (term: string) => {
      if (!term) return;
      const items = getRecentSearches();
      const next = [
        term,
        ...items.filter((i) => i.toLowerCase() !== term.toLowerCase()),
      ];
      setRecentSearches(next);
    },
    [getRecentSearches, setRecentSearches]
  );

  const handleMobileNavigation = useCallback(
    (path: string) => {
      requestAnimationFrame(() => {
        navigate(path);
        setMobileMenuOpen(false);
        setShowSearchDropdown(false);
        setShowMobileSearch(false);
      });
    },
    [navigate]
  );

  // Optimized mobile menu click outside handler
  useEffect(() => {
    const handleClickOutsideMobileMenu = (event: MouseEvent) => {
      const target = event.target as Node;
      const mobileMenu = document.querySelector(".mobile-menu");
      const menuButton = document.querySelector(".mobile-menu-button");

      if (
        mobileMenuOpen &&
        mobileMenu &&
        !mobileMenu.contains(target) &&
        menuButton &&
        !menuButton.contains(target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideMobileMenu, {
      passive: true,
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideMobileMenu);
    };
  }, [mobileMenuOpen]);

  const user = useAuthStore((state) => state.user);

  if (!isMounted) {
    return <div className="h-16 bg-gray-100 animate-pulse"></div>;
  }

  if (
    pathname.includes("/login") ||
    pathname.includes("/register") ||
    pathname.includes("/checkout") ||
    pathname.includes("/otplogin") ||
    pathname.includes("/forgot") ||
    (pathname.includes("/contactus") && !user)
  ) {
    return null;
  }

  return (
    <>
      {/* Top Banner */}
      <TopBanner />

      <div className="hidden md:block">
        <AddressAndLanguage />
      </div>

      {/* Main Header & Mobile Menu Container */}
      <div className="sticky top-0 z-50 w-full">
        <div className="w-full bg-red-500 md:bg-white border-b relative">
          <MaxWidthWrapper className="max-w-full">
            <div className="mx-auto flex items-center justify-between py-1 md:py-4">
              {/* Left - Mobile Menu & Logo */}
              <div className="flex items-center gap-3 md:gap-4">
                <button
                  className="mobile-menu-button md:hidden p-2 hover:bg-gray-100 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Toggle menu"
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? (
                    <X
                      size={24}
                      className="text-white transition-transform duration-300 ease-in-out"
                    />
                  ) : (
                    <Menu
                      size={24}
                      className="text-white transition-transform duration-300 ease-in-out"
                    />
                  )}
                </button>
                <Link 
                  className="cursor-pointer group" 
                  to="/"
                  onClick={handleLogoClick}
                >
                  <img
                    src="/caremall.png"
                    alt="Caremall Logo"
                    className="hidden md:block w-20 md:w-30 lg:w-40 xl:w-45 transition-transform duration-200 group-hover:scale-105"
                  // loading="lazy"
                  />
                  <img
                    src="/white-logo.png"
                    alt="Caremall Logo"
                    className="md:hidden w-30 transition-transform duration-200 group-hover:scale-105"
                  // loading="lazy"
                  />
                </Link>
              </div>

              {/* Center - Search Bar */}
              <div className="hidden md:flex flex-1 max-w-2xl mx-4 lg:mx-8">
                <div className="relative w-full" ref={desktopSearchRef}>
                  <div className="relative flex items-center bg-gray-50 border-2 border-gray-200 rounded-lg px-4 py-2.5 w-full focus-within:border-red-500 focus-within:bg-white transition-all duration-200">
                    <Search size={18} className="text-gray-500 mr-3" />
                    <Input
                      type="text"
                      placeholder={t("searchPlaceholder")}
                      className="border-none focus-visible:ring-0 px-0 py-0 h-6 flex-1 shadow-none rounded-none bg-transparent text-gray-900 placeholder-gray-500 text-sm"
                      value={searchTerm}
                      onFocus={() => setShowSearchDropdown(true)}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowSearchDropdown(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setShowSearchDropdown(false);
                          navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
                        }
                      }}
                    />
                  </div>

                  {/* Search Dropdown */}
                  {showSearchDropdown && (
                    <div
                      className="absolute top-full left-0 mt-2 bg-white shadow-xl border border-gray-200 rounded-lg z-50 w-full max-h-[500px] overflow-y-auto"
                      style={{ willChange: "transform, opacity" }}
                    >
                      <SearchDropdownContent
                        searchTerm={searchTerm}
                        isFetching={isFetching}
                        searchResults={searchResults}
                        mostWanted={mostWanted}
                        recentSearches={getRecentSearches()}
                        onTermClick={(term) => {
                          setSearchTerm(term);
                          addRecentSearch(term);
                        }}
                        onResultClick={(path, term) => {
                          handleMobileNavigation(path);
                          addRecentSearch(term);
                          setSearchTerm("");
                          setShowSearchDropdown(false);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Right - Action Icons */}
              <div className="flex items-center gap-1 sm:gap-2">
                {/* Mobile Search Button */}
                <button
                  className="md:hidden p-2 hover:bg-gray-50 rounded-md transition-all duration-200"
                  onClick={() => setShowMobileSearch(!showMobileSearch)}
                  aria-label="Search"
                >
                  <Search size={20} className="text-white font-medium" />
                </button>

                <div className="hidden md:flex gap-4 lg:gap-5">
                  <ScrollToSection
                    sectionId="newarrivals"
                    label={t("newArrivals")}
                  />
                  <ScrollToSection
                    sectionId="mostwanted"
                    label={t("mostWanted")}
                  />

                  {/* Categories Dropdown for Desktop */}
                  <CategoriesDropdown />
                </div>

                {/* Wishlist */}
                <WishlistPopover />

                {/* Cart */}
                <CartPopover />

                {/* User Profile */}
                <UserMenu />
              </div>
            </div>

            {/* Mobile Search Bar */}
            {showMobileSearch && (
              <div className="md:hidden px-4 pb-3" ref={mobileSearchRef}>
                <div className="relative flex items-center bg-gray-50 border-2 border-gray-200 rounded-lg px-4 py-2.5 w-full focus-within:border-red-500 focus-within:bg-white transition-all duration-200 shadow-sm">
                  <Search size={18} className="text-gray-500 mr-3" />
                  <Input
                    type="text"
                    placeholder={t("searchPlaceholder")}
                    className="border-none focus-visible:ring-0 px-0 py-0 h-6 flex-1 shadow-none rounded-none bg-transparent text-gray-900 placeholder-gray-500 text-sm"
                    value={searchTerm}
                    onFocus={() => setShowSearchDropdown(true)}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowSearchDropdown(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setShowSearchDropdown(false);
                        setShowMobileSearch(false);
                        navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
                      }
                    }}
                  />
                  <button
                    onClick={() => setShowMobileSearch(false)}
                    className="ml-2 p-1 hover:bg-gray-200 rounded-md transition-colors duration-200"
                  >
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>

                {/* Mobile Search Dropdown */}
                {showSearchDropdown && (
                  <div
                    className="absolute top-full left-0 bg-white mx-3 shadow-xl border border-gray-200 rounded-lg z-50 w-full max-h-[400px] overflow-y-auto"
                    style={{ willChange: "transform, opacity" }}
                  >
                    <SearchDropdownContent
                      searchTerm={searchTerm}
                      isFetching={isFetching}
                      searchResults={searchResults}
                      mostWanted={mostWanted}
                      recentSearches={getRecentSearches()}
                      onTermClick={(term) => {
                        setSearchTerm(term);
                        addRecentSearch(term);
                      }}
                      onResultClick={(path, term) => {
                        handleMobileNavigation(path);
                        addRecentSearch(term);
                        setSearchTerm("");
                        setShowSearchDropdown(false);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </MaxWidthWrapper>
        </div>

        {/* Enhanced Mobile Menu */}
        <MobileMenu
          ref={mobileMenuRef}
          isOpen={mobileMenuOpen}
          onNavigate={handleMobileNavigation}
        />
      </div>
    </>
  );
};

export default Header;
